import React, { useEffect, useMemo, useRef, useState } from "react";
import { CardPosition, Market } from "types/watchlist.types";
import { FixMeLater } from "types";
import { motion, useAnimationControls } from "motion/react";
import SymbolCard from "features/watchlist/WatchlistCards/SymbolCard";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_MENU } from "common/constants";
import { CoinName, ExchangeName } from "@shared/types";

interface AnimatedSymbolCardProps {
  watchlistMarket: Market;
  onRemove: (symbol: CoinName, exchange: ExchangeName) => void;
  setPosition: (i: number, offset: CardPosition) => void;
  moveItem: (i: number, yOffset: number, xOffset: number) => void;
  i: number;
  replaceItems: (i: number) => void;
  constraintsRef: React.RefObject<any>;
}

const AnimatedSymbolCard: React.FC<AnimatedSymbolCardProps> = ({
  watchlistMarket,
  onRemove,
  setPosition,
  moveItem,
  i,
  replaceItems,
  constraintsRef,
}) => {
  const isTablet = useMedia([`(max-width: ${MOBILE_MENU})`], [true], false);
  const [isDragging, setIsDragging] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [startIndex, setStartIndex] = useState(-1);
  const ref = useRef(null as FixMeLater);
  const animationControls = useAnimationControls();

  const onReset = () => {
    animationControls
      .start({
        x: 0,
        y: 0,
        transition: { duration: 0.25 },
      })
      .catch(() => {});
  };

  useEffect(() => {
    setPosition(i, {
      top: ref.current.offsetTop,
      left: ref.current.offsetLeft,
      height: ref.current.offsetHeight,
      width: ref.current.offsetWidth,
    });
  }, []);

  const symbolCard = useMemo(
    () => (
      <SymbolCard
        watchlistMarket={watchlistMarket}
        onRemove={onRemove}
        ignoreOnClick={isAnimating}
      />
    ),
    [watchlistMarket, isAnimating]
  );

  return useMemo(
    () => (
      <motion.div
        style={{
          position: "relative",
          pointerEvents: isAnimating ? "none" : "auto",
          zIndex: isAnimating ? 1 : 0,
        }}
        ref={ref}
        initial={false}
        animate={{ ...animationControls, ...(isDragging ? onTop : flat) }}
        whileTap={{ scale: isTablet ? 1 : 1.05 }}
        drag={!isTablet}
        dragConstraints={constraintsRef}
        layout
        dragElastic={0.1}
        onDragStart={() => {
          setIsDragging(true);
          setIsAnimating(true);
          setStartIndex(i);
        }}
        onDragEnd={() => {
          setIsDragging(false);
          setTimeout(() => setIsAnimating(false), 600);
          startIndex !== i && replaceItems(i);
          onReset();
        }}
        onUpdate={(data) => {
          isDragging && moveItem(i, data.y as number, data.x as number);
        }}
      >
        {symbolCard}
      </motion.div>
    ),
    [i, isDragging, watchlistMarket, isAnimating, isTablet]
  );
};

export default AnimatedSymbolCard;

const onTop = { zIndex: 1 };
const flat = {
  zIndex: 0,
  transition: { delay: 0.3 },
};
